import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getCurrentUser as getCurrentUserApi,
  signIn as signInApi,
  signOut as signOutApi,
  signUp as signUpApi,
  type User,
  type SignUpResponse,
  type AuthResponse,
} from '../screens/auth/api';
import { tokenStorage, ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '../lib/token-storage';
import { disableGoogleAutoSelect } from '../lib/google-auth';
import { useApiQuery } from '../hooks/useApiQuery';
import { postHogService } from '../lib/posthog';
import { AnalyticsEvents } from '@/lib/event-constants';
import { useLocale } from './LocaleContext';

interface AuthError {
  message: string;
  detail?: unknown;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string, inviteToken?: string | null) => Promise<{ error: AuthError | null; data: SignUpResponse | AuthResponse | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null; user: User | null }>;
  signInWithTokens: (accessToken: string, refreshToken: string, context: 'signin' | 'signup') => Promise<User | null>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { setLocale } = useLocale();
  const [hasToken, setHasToken] = useState(() => !!tokenStorage.getAccessToken());

  const {
    data: user = null,
    isLoading: isQueryLoading,
    isError,
    refetch,
  } = useApiQuery(['currentUser'], getCurrentUserApi, {
    enabled: hasToken,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });

  const loading = hasToken && isQueryLoading;

  useEffect(() => {
    if (hasToken && (isError && user === null)) {
      tokenStorage.clearTokens();
    }
  }, [hasToken, isQueryLoading, isError, user]);

  // Identify user in PostHog when user data is available
  useEffect(() => {
    if (user) {
      postHogService.identifyUser(user.id, {
        email: user.email,
        created_at: user.created_at,
        // Add any other user properties you want to track
      });
    }
  }, [user]);

  useEffect(() => {
    if (user?.locale) {
      setLocale(user.locale);
    }
  }, [user?.locale, setLocale]);


  useEffect(() => {
    // Subscribe to token changes (e.g., when http-client clears tokens after failed refresh)
    const unsubscribe = tokenStorage.subscribe((tokenExists) => {
      setHasToken(tokenExists);
      if (!tokenExists) {
        queryClient.setQueryData(['currentUser'], null);
      }
    });

    // Handle cross-tab synchronization
    const handleStorageChange = (event: StorageEvent) => {
      // Only care about our token keys
      if (event.key === ACCESS_TOKEN_KEY || event.key === REFRESH_TOKEN_KEY) {
        const token = tokenStorage.getAccessToken();
        const tokenExists = !!token;
        setHasToken(tokenExists);

        if (!token) {
          // User logged out in another tab
          queryClient.setQueryData(['currentUser'], null);
        } else {
          // User logged in from another tab, reload user
          void refetch();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [refetch, queryClient]);

  const signUp = async (email: string, password: string, inviteToken?: string | null) => {
    try {
      const payload = inviteToken ? { email, password, invite_token: inviteToken } : { email, password };
      const data = await signUpApi(payload);

      if ('access_token' in data && data.access_token) {
        tokenStorage.setTokens(data.access_token, data.refresh_token);
        const nextUser = (data.user ?? null) as User | null;
        queryClient.setQueryData(['currentUser'], nextUser);
        postHogService.trackEvent(AnalyticsEvents.USER_SIGNED_UP, { method: 'email' });
      }

      return { error: null, data };
    } catch (error) {
      return {
        error: {
          message: error instanceof Error ? error.message : 'An unexpected error occurred',
          detail: error instanceof Error && 'detail' in error ? (error as { detail: string }).detail : undefined
        },
        data: null,
      };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const response = await signInApi({ email, password });
      tokenStorage.setTokens(response.access_token, response.refresh_token);
      const nextUser = (response.user ?? null) as User | null;
      queryClient.setQueryData(['currentUser'], nextUser);
      postHogService.trackEvent(AnalyticsEvents.USER_LOGGED_IN, { method: 'email' });
      return { error: null, user: nextUser };
    } catch (error) {
      return {
        error: {
          message: error instanceof Error ? error.message : 'An unexpected error occurred',
          detail: error instanceof Error && 'detail' in error ? (error as { detail: string }).detail : undefined
        },
        user: null,
      };
    }
  };

  const signInWithTokens = async (accessToken: string, refreshToken: string, context: 'signin' | 'signup') => {
    tokenStorage.setTokens(accessToken, refreshToken);
    const { data } = await refetch();
    if (context === 'signin') {
      postHogService.trackEvent(AnalyticsEvents.USER_LOGGED_IN, { method: 'google' });
    } else {
      postHogService.trackEvent(AnalyticsEvents.USER_SIGNED_UP, { method: 'google' });
    }
    return data ?? null;
  };

  const signOut = async () => {
    await signOutApi();
    disableGoogleAutoSelect();
    tokenStorage.clearTokens();
    postHogService.trackEvent(AnalyticsEvents.USER_LOGGED_OUT);
    queryClient.setQueryData(['currentUser'], null);

    // Reset PostHog to clear user identification
    postHogService.reset();
  };

  const refreshUser = async () => {
    const { data } = await refetch();
    return data ?? null;
  };

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signInWithTokens,
    signOut,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
